import React, { useState } from 'react';
import { Settings, User, MapPin, Crown, Mail, Save, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader, InputField } from '../components/Card';

const PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Nova Scotia', 'Ontario',
  'Prince Edward Island', 'Quebec', 'Saskatchewan',
  'Northwest Territories', 'Nunavut', 'Yukon',
];

export default function SettingsPage() {
  const { settings, setSettings, showToast } = useApp();
  const [saved, setSaved] = useState(false);

  const update = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveProfile = () => {
    setSaved(true);
    showToast('Profile saved!');
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTogglePro = () => {
    const newPro = !settings.isPro;
    setSettings(prev => ({ ...prev, isPro: newPro }));
    showToast(newPro ? 'Upgraded to Pro!' : 'Downgraded to Free');
  };

  return (
    <div>
      <PageHeader
        icon={<Settings size={22} />}
        title="Settings"
        subtitle="Manage your profile and preferences"
      />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <User size={20} color="var(--gray-500)" />
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Profile Information</h3>
            <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>This information will appear on your exported reports</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <InputField label="Full Name" placeholder="Your name"
            value={settings.fullName}
            onChange={e => update('fullName', e.target.value)} />
          <InputField label="Email" placeholder="your@email.com" type="email"
            value={settings.email}
            onChange={e => update('email', e.target.value)} />
        </div>
        <InputField label="Phone" placeholder="(555) 123-4567"
          value={settings.phone}
          onChange={e => update('phone', e.target.value)}
          style={{ maxWidth: '50%' }} />
        <div style={{ marginTop: 16 }}>
          <button onClick={handleSaveProfile} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: saved ? 'var(--green)' : 'var(--primary)',
            color: 'white', borderRadius: 'var(--radius-sm)',
            fontWeight: 600, fontSize: 14, transition: 'background 0.2s',
          }}>
            {saved ? <><CheckCircle size={16} /> Saved!</> : <><Save size={16} /> Save Profile</>}
          </button>
        </div>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <MapPin size={20} color="var(--gray-500)" />
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Location</h3>
            <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>Used for region-specific calculations and checklists</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 6 }}>Province</label>
            <select
              value={settings.province}
              onChange={e => update('province', e.target.value)}
              style={{
                width: '100%', padding: '10px 14px',
                border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                fontSize: 15, background: 'white',
              }}
            >
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <InputField label="City" placeholder="Your city"
            value={settings.city}
            onChange={e => update('city', e.target.value)} />
        </div>
        <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Ottawa-specific content</span>
            <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '4px 0 0' }}>Show Ottawa-specific inspection tips, insurance info, and local details</p>
          </div>
          <button
            onClick={() => update('isOttawa', !settings.isOttawa)}
            style={{
              width: 44, height: 24, borderRadius: 12, padding: 2,
              background: settings.isOttawa ? 'var(--green)' : 'var(--gray-300)',
              border: 'none', cursor: 'pointer', transition: 'background 0.2s',
              display: 'flex', alignItems: 'center',
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: '50%', background: 'white',
              transition: 'transform 0.2s',
              transform: settings.isOttawa ? 'translateX(20px)' : 'translateX(0)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
      </Card>

      <Card style={{ marginBottom: 20, borderLeft: `4px solid ${settings.isPro ? 'var(--green)' : 'var(--primary)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Crown size={20} color={settings.isPro ? 'var(--green)' : 'var(--primary)'} />
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Subscription Plan</h3>
            <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              Current tier: <strong style={{ color: settings.isPro ? 'var(--green)' : 'inherit' }}>
                {settings.isPro ? 'Pro' : 'Free'}
              </strong>
            </p>
          </div>
        </div>
        <p style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 16 }}>
          {settings.isPro
            ? 'You are currently on the Pro plan. All premium features are unlocked.'
            : 'Upgrade to Pro to unlock all premium features including advanced reports, unlimited scenarios, and priority support.'}
        </p>
        <button onClick={handleTogglePro} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 20px', background: settings.isPro ? 'var(--gray-200)' : 'var(--primary)',
          color: settings.isPro ? 'var(--gray-700)' : 'white', borderRadius: 'var(--radius-sm)',
          fontWeight: 600, fontSize: 14, cursor: 'pointer',
        }}>
          <Crown size={16} /> {settings.isPro ? 'Downgrade to Free' : 'Upgrade to Pro'}
        </button>
      </Card>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Mail size={20} color="var(--gray-500)" />
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Invite Test Users</h3>
            <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>Send invitations to testers who can create accounts and try the app</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email Address</label>
            <input type="email" placeholder="tester@example.com"
              style={{
                width: '100%', padding: '10px 14px',
                border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14,
              }} />
          </div>
          <div style={{ minWidth: 120 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
            <select style={{
              width: '100%', padding: '10px 14px',
              border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white',
            }}>
              <option>User</option>
              <option>Admin</option>
            </select>
          </div>
          <button style={{
            padding: '10px 20px', background: 'var(--primary)',
            color: 'white', borderRadius: 'var(--radius-sm)',
            fontWeight: 600, fontSize: 14, height: 42,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Mail size={14} /> Send Invitation
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 12 }}>
          Invited users will receive an email to create their account and access the app.
        </p>
      </Card>
    </div>
  );
}
